import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/DevelopmentForm.css";
import { developmentWorksAPI, fileAPI, galleryAPI } from "../../api/index";

const MANDALS = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];
const CATEGORIES = ["Road Works", "Water Supply", "Drainage"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

// /files/upload only accepts fixed subfolders (gallery, voters, press, ...).
// Development images are stored in the Gallery, so we use "gallery".
const UPLOAD_SUBFOLDER = "gallery";

const initialForm = {
  mandal_name: "",
  village_name: "",
  category: "",
  title: "",
  estimated_cost: "",
  description: "",
  status: "pending", // pending | in_progress | completed
  work_date: "",
};

function AdminDevelopmentForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [images, setImages] = useState([]); // { file, url, name, status: 'pending'|'uploading'|'done'|'error' }
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // free preview URLs when leaving the page
  const imagesRef = useRef(images);
  imagesRef.current = images;
  useEffect(() => {
    return () => imagesRef.current.forEach((im) => URL.revokeObjectURL(im.url));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------- images ---------- */
  const addFiles = (fileList) => {
    const valid = [];
    for (const file of Array.from(fileList || [])) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG, WEBP allowed`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 5MB and was skipped`);
        continue;
      }
      valid.push({ file, url: URL.createObjectURL(file), name: file.name, status: "pending" });
    }
    if (valid.length) setImages((prev) => [...prev, ...valid]);
  };

  const handleFilesSelected = (e) => {
    addFiles(e.target.files);
    e.target.value = ""; // allows picking the same file again
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeImage = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleReset = () => {
    images.forEach((im) => URL.revokeObjectURL(im.url));
    setForm(initialForm);
    setImages([]);
  };

  // 1) POST /files/upload?subfolder=gallery  -> returns the stored file path
  // 2) POST /gallery { title, photo_url, description }  -> registers the photo
  // Returns how many images failed.
  const uploadImages = async (workTitle) => {
    let failed = 0;

    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, status: "uploading" } : im)));

      try {
        const uploadRes = await fileAPI.upload(img.file, UPLOAD_SUBFOLDER);

        const d = uploadRes.data;
        const fileUrl =
          typeof d === "string" ? d : d?.url || d?.file_url || d?.path || d?.file_path;
        if (!fileUrl) throw new Error("Upload did not return a file URL");

        // Gallery schema requires "photo_url" (not "image_url")
        await galleryAPI.create({
          title: `${workTitle} — photo ${i + 1}`,
          photo_url: fileUrl,
          description: `Development work photo: ${workTitle}`,
        });

        setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, status: "done" } : im)));
      } catch (err) {
        failed += 1;
        console.error(`Image upload failed for ${img.name}:`, err.response?.data || err.message);
        setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, status: "error" } : im)));
      }
    }
    return failed;
  };

  /* ---------- submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only title and mandal_name are required by DevelopmentWorkCreate
    if (!form.mandal_name || !form.title.trim()) {
      toast.error("Development Title and Mandal are required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        mandal_name: form.mandal_name,
        status: form.status,
      };
      if (form.category) payload.category = form.category;
      if (form.village_name.trim()) payload.village_name = form.village_name.trim();
      if (form.description.trim()) payload.description = form.description.trim();
      if (form.estimated_cost !== "") payload.estimated_cost = Number(form.estimated_cost);
      if (form.work_date) payload.work_date = form.work_date;

      await developmentWorksAPI.create(payload);

      if (images.length > 0) {
        toast.info(`Uploading ${images.length} image(s)...`);
        const failed = await uploadImages(form.title.trim());
        if (failed > 0) {
          toast.warning(`Work saved, but ${failed} image(s) could not be uploaded`);
        } else {
          toast.success("Development work and images added successfully");
        }
      } else {
        toast.success("Development work added successfully");
      }

      handleReset();
      navigate("/AdminDashboard/development");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || d.reason).join("; ")
          : "Could not save development work"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="development-form">
      <div className="form-header">
        <div>
          <h2>Add Development Work</h2>
          <p>Enter development details and upload images to display on website</p>
        </div>
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Mandal / Area *</label>
            <select name="mandal_name" value={form.mandal_name} onChange={handleChange} required>
              <option value="">Select Mandal</option>
              {MANDALS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Category</label>
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">Select Category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Village</label>
            <input
              type="text"
              name="village_name"
              placeholder="Enter village name (must match an existing village)"
              value={form.village_name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Development Title *</label>
            <input
              type="text"
              name="title"
              placeholder="Enter title of the development work"
              value={form.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Estimated Cost (₹)</label>
            <input
              type="number"
              min="0"
              name="estimated_cost"
              placeholder="Enter estimated cost"
              value={form.estimated_cost}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="pending">Proposed / Pending</option>
              <option value="in_progress">Ongoing</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="form-group">
            <label>Work Date</label>
            <input type="date" name="work_date" value={form.work_date} onChange={handleChange} />
          </div>

          <div className="form-group full-width">
            <label>Description</label>
            <textarea
              rows="5"
              name="description"
              placeholder="Enter description of the development work"
              value={form.description}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <div className="upload-box">
            <h4>Upload Images</h4>
            <div
              className="drop-zone"
              style={dragging ? { borderColor: "#2563eb", background: "#eff6ff" } : undefined}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <p>Drag & Drop Images Here</p>
              <input
                type="file"
                id="images"
                multiple
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={handleFilesSelected}
              />
              <label htmlFor="images" className="choose-btn">Choose Files</label>
              <small>JPG, PNG, WEBP (Max 5MB each) — uploaded to Gallery on submit</small>
            </div>
          </div>

          <div className="preview-box">
            <h4>Uploaded Images ({images.length})</h4>
            {images.length === 0 ? (
              <div className="preview-area">No Images Uploaded</div>
            ) : (
              <div className="preview-area" style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {images.map((img, i) => (
                  <div key={img.url} style={{ position: "relative", width: 80, height: 80 }}>
                    <img
                      src={img.url}
                      alt={img.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 8,
                        opacity: img.status === "error" ? 0.4 : 1,
                        border:
                          img.status === "done" ? "2px solid #16a34a"
                          : img.status === "error" ? "2px solid #dc2626"
                          : img.status === "uploading" ? "2px solid #eab308"
                          : "2px solid transparent",
                      }}
                    />
                    {img.status === "uploading" && (
                      <div
                        style={{
                          position: "absolute", inset: 0, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          background: "rgba(0,0,0,0.35)", borderRadius: 8, color: "#fff", fontSize: 10,
                        }}
                      >
                        ⏳
                      </div>
                    )}
                    {img.status === "pending" && !submitting && (
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        aria-label="Remove image"
                        style={{
                          position: "absolute", top: -6, right: -6, width: 20, height: 20,
                          borderRadius: "50%", background: "#dc2626", color: "#fff",
                          border: "none", fontSize: 12, cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="button-group">
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
          <button type="button" className="reset-btn" onClick={handleReset} disabled={submitting}>
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminDevelopmentForm;